/**
 * Organization Switcher Component
 *
 * Allows superadmin users to switch between organizations.
 * Non-superadmin users see a static organization label.
 * Always renders in expanded layout (icon + name + chevron).
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useActiveOrganization } from '@/hooks/useActiveOrganization';
import { useAllOrgsQuery } from '@/hooks/queries';
import { useSidebar } from '@/hooks/useSidebar';
import { DashboardTab } from '@/types/dashboard';
import { Icon, IconName } from '@/icons';
import { Avatar } from '@/components/ui/avatar';
import { useThemeColors } from '@/styles';
import { sizing, layout } from '@/styles/design-tokens';
import type { Organization } from '@/types/models/Organization';

export function OrgSwitcher() {
  const { user } = useAuth();
  const {
    activeOrganizationId,
    setActiveOrganizationId,
    isViewingDifferentOrg,
  } = useActiveOrganization();
  const { setActiveTab } = useSidebar();
  const colors = useThemeColors();
  const { data: orgsData, isLoading } = useAllOrgsQuery();

  const organizations = useMemo(() => {
    const orgs = (orgsData?.organizations ?? []) as Organization[];
    return [...orgs].sort((a, b) => a.title.localeCompare(b.title));
  }, [orgsData]);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonPressed, setIsButtonPressed] = useState(false);
  const [hoveredOrgId, setHoveredOrgId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const previousOrgIdRef = useRef<string | undefined>(undefined);

  const isSuperadmin = user?.organization?.is_superadmin_org === true;

  // Automatically navigate when switching organizations
  useEffect(() => {
    if (!isSuperadmin) return;

    const orgChanged =
      previousOrgIdRef.current !== undefined &&
      previousOrgIdRef.current !== activeOrganizationId;

    if (orgChanged) {
      if (isViewingDifferentOrg) {
        setActiveTab(DashboardTab.DASHBOARD);
      } else {
        setActiveTab(DashboardTab.ORGS_MANAGEMENT);
      }
    }

    previousOrgIdRef.current = activeOrganizationId;
  }, [activeOrganizationId, isViewingDifferentOrg, isSuperadmin, setActiveTab]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Find current organization
  const currentOrg =
    organizations.find(org => org.id === activeOrganizationId) ||
    user?.organization;

  const handleOrgSwitch = (orgId: string) => {
    setActiveOrganizationId(orgId);
    setIsDropdownOpen(false);
  };

  // Non-superadmin static display
  if (!isSuperadmin) {
    return (
      <div
        className="flex items-center gap-2 px-3 rounded"
        style={{ backgroundColor: colors.offWhite, height: sizing.button.lg }}
      >
        <Avatar
          src={user?.organization?.profile_image}
          displayName={user?.organization?.title || 'Organization'}
          userId={user?.organization?.id || 'org'}
          size={24}
        />
        <span
          className="text-sm font-medium truncate"
          style={{ color: colors.text.primary }}
        >
          {user?.organization?.title || 'Organization'}
        </span>
      </div>
    );
  }

  // Superadmin switchable display
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        className="flex items-center justify-between w-full px-3 rounded transition-all duration-200"
        style={{
          height: sizing.button.lg,
          backgroundColor: isButtonPressed
            ? colors.active.background
            : isButtonHovered
              ? colors.hover.background
              : colors.offWhite,
          color: isButtonPressed
            ? colors.active.text
            : isButtonHovered
              ? colors.hover.text
              : colors.text.primary,
        }}
        onMouseEnter={() => setIsButtonHovered(true)}
        onMouseLeave={() => {
          setIsButtonHovered(false);
          setIsButtonPressed(false);
        }}
        onMouseDown={() => setIsButtonPressed(true)}
        onMouseUp={() => setIsButtonPressed(false)}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Avatar
            src={currentOrg?.profile_image}
            displayName={currentOrg?.title || 'Organization'}
            userId={currentOrg?.id || 'org'}
            size={24}
          />
          <span className="text-sm font-medium truncate">
            {currentOrg?.title || 'Organization'}
          </span>
        </div>
        <Icon
          name={isDropdownOpen ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN}
          size="sm"
          style={{
            color:
              isButtonPressed || isButtonHovered
                ? 'currentColor'
                : colors.text.secondary,
          }}
        />
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsDropdownOpen(false)}
          />

          {/* Dropdown menu */}
          <div
            className="absolute bottom-full left-0 mb-2 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
            style={{
              width: layout.sidebar.width,
              backgroundColor: colors.background,
              border: `1px solid ${colors.border.default}`,
            }}
          >
            {isLoading ? (
              <div
                className="px-4 py-3 text-sm"
                style={{ color: colors.text.secondary }}
              >
                Loading organizations...
              </div>
            ) : organizations.length === 0 ? (
              <div
                className="px-4 py-3 text-sm"
                style={{ color: colors.text.secondary }}
              >
                No organizations found
              </div>
            ) : (
              <div className="py-2">
                {organizations.map(org => {
                  const isActive = org.id === activeOrganizationId;
                  const isHovered = hoveredOrgId === org.id;
                  return (
                    <button
                      key={org.id!}
                      onClick={() => handleOrgSwitch(org.id!)}
                      className="w-full flex items-center gap-3 px-4 transition-all duration-150 hover:translate-x-1"
                      style={{
                        height: sizing.button.lg,
                        backgroundColor: isActive
                          ? colors.active.background
                          : isHovered
                            ? colors.hover.background
                            : 'transparent',
                        color: isActive
                          ? colors.active.text
                          : isHovered
                            ? colors.hover.text
                            : colors.text.primary,
                      }}
                      onMouseEnter={() => setHoveredOrgId(org.id ?? null)}
                      onMouseLeave={() => setHoveredOrgId(null)}
                    >
                      <Avatar
                        src={org.profile_image}
                        displayName={org.title}
                        userId={org.id!}
                        size={24}
                      />
                      <span className="flex-1 text-left text-sm truncate">
                        {org.title}
                      </span>
                      {isActive && (
                        <Icon
                          name={IconName.CHECK}
                          size="sm"
                          style={{ color: colors.active.text }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
